import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { closeRoadStretchMediasDetailsForm } from "../../../actions/org/OrgRoadStretchActions";
import { getRoadStretchesByCityId } from "../../../prooh/actions/campaign-planning/CampaignPlanningRoadStretchesActions";

// Constants and Utils
import { FormDataTargets } from "../../../constants/GeneralConstants";
import { toStringWithRoundUp } from "../../../common-utils/number-utils/NumberUtils";
import {
  formatText,
  toLocaleString,
} from "../../../common-utils/string-utils/StringUtils";
import { LoadingData } from "../OrgUtils";

// Components
import TableHeaders from "../../../components/table/TableHeaders";

const tableHeaders = [
  {
    title: {
      displayName: "Site Name",
      className: "col-6",
    },
  },
  {
    title: {
      displayName: "Device ID",
      className: "col-3 text-center",
    },
  },
  {
    title: {
      displayName: "Site MOP",
      className: "col-3 text-center",
    },
  },
];

function RoadStretchMediasDataBody({ stretchId, stretchName }) {
  // selector data
  const roadStretchesByCityIdLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );

  // stretch details map (overall stretch data including all medias)
  const roadStretchDetailsMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // selected stretch info
  const selectedStretchInfo = roadStretchDetailsMap[stretchId] || {};
  const { orgMedias = [], avgMOP } = selectedStretchInfo || {};
  const orgMediasLength = orgMedias ? orgMedias.length : 0;
  const isStretchMediasPresent = orgMediasLength > 0;
  const formattedStretchAvgMOP =
    avgMOP === 0 ? 0 : formatText(toStringWithRoundUp(avgMOP));

  // loading
  if (roadStretchesByCityIdLoading) {
    return <LoadingData className="p-1" dataType="Road Stretch Medias" />;
  }

  // no data message
  if (!isStretchMediasPresent) {
    return (
      <p className="font-italic my-3 text-center">
        <span>No Media Sites present on Road Stretch : </span>
        <br />
        <span className="font-weight-bold">{stretchName}</span>
      </p>
    );
  }

  // data
  return (
    <>
      <div className="mb-3">
        <h5 className="font-weight-bold mb-0">Stretch Details </h5>
        <p className="mb-0">Total Sites: {orgMediasLength}</p>
        <p className="mb-0">Avg. MOP: {formattedStretchAvgMOP}</p>
      </div>

      <div>
        <h5 className="font-weight-bold mb-1">Stretch Medias Details</h5>
        <div className="table-responsive max-h-400">
          <table className="table">
            <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
            <tbody>
              {orgMedias.map((media) => {
                const { id, title, sourceId, pricing = {} } = media;
                const price = pricing?.price || 0;
                const formattedPrice = price === 0 ? 0 : toLocaleString(price);
                return (
                  <tr key={id}>
                    <td>
                      <p>{title}</p>
                    </td>
                    <td className="text-center">
                      <p>{sourceId}</p>
                    </td>
                    <td className="text-center">
                      <p>{formattedPrice}</p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * Road Stretch Medias Details Form
 */
export default function RoadStretchMediasDetailsForm() {
  const dispatch = useDispatch();

  // stretch info
  const stretchInfo = useSelector(
    (state) => state.orgRoadStretch.roadStretchMediaDetailsProcessDataObject
  );
  const { cityId, id: stretchId = "", name: stretchName } = stretchInfo || {};

  // road stretches data (including medias) for city
  useEffect(() => {
    dispatch(getRoadStretchesByCityId(cityId));
  }, [dispatch, cityId]);

  // functions
  function onCancel() {
    dispatch(closeRoadStretchMediasDetailsForm());
  }

  return (
    <div
      className="modal fade"
      id={FormDataTargets.roadStretchMediasDetailsForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">Road Stretch Medias Details</h4>
          </div>

          {/* modal body */}
          <div className="modal-body px-5 pt-4 mt-2 min-h-400">
            <RoadStretchMediasDataBody
              stretchId={stretchId}
              stretchName={stretchName}
            />
          </div>

          {/* Form Footer */}
          <div className="modal-footer border-0 px-5">
            <button
              type="button"
              className="btn btn-outline-primary rounded-lg shadow-none"
              data-dismiss="modal"
              onClick={onCancel}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
